import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { buyerQueriesService } from '../../../services/supabaseService';

const ContactForm = ({ language }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: '',
    attachment: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const content = {
    en: {
      title: 'Send Us a Message',
      subtitle: 'Fill out the form below and our team will get back to you within 24 hours',
      name: 'Full Name',
      namePlaceholder: 'Enter your full name',
      email: 'Email Address',
      emailPlaceholder: 'your.name@africcoffee.com',
      phone: 'Phone Number',
      phonePlaceholder: '+8619706820472',
      company: 'Company Name',
      companyPlaceholder: 'Your company name',
      inquiryType: 'Inquiry Type',
      inquiryPlaceholder: 'Select inquiry type',
      message: 'Message',
      messagePlaceholder: 'Tell us about your inquiry in detail...',

      attachment: 'Attach Document',
      attachmentDesc: 'Optional: Upload specifications or related documents (PDF, DOC, max 5MB)',
      submit: 'Send Message',
      submitting: 'Sending...',
      successTitle: 'Message Sent Successfully!',
      successMessage: 'Thank you for contacting us. We will respond within 24 hours.',
      sendAnother: 'Send Another Message',
      inquiryTypes: [
        { value: 'buyer', label: 'Buyer Interest - Coffee Purchase' },
        { value: 'supplier', label: 'Supplier Application' },
        { value: 'partnership', label: 'Partnership Opportunity' },
        { value: 'general', label: 'General Inquiry' }
      ]
    },
    zh: {
      title: '发送消息',
      subtitle: '填写下面的表格，我们的团队将在24小时内回复您',
      name: '全名',
      namePlaceholder: '输入您的全名',
      email: '电子邮件地址',
      emailPlaceholder: 'your.name@africcoffee.com',
      phone: '电话号码',
      phonePlaceholder: '+8619706820472',
      company: '公司名称',
      companyPlaceholder: '您的公司名称',
      inquiryType: '咨询类型',
      inquiryPlaceholder: '选择咨询类型',
      message: '消息',
      messagePlaceholder: '详细告诉我们您的咨询...',

      attachment: '附加文档',
      attachmentDesc: '可选：上传规格或相关文档（PDF、DOC，最大5MB）',
      submit: '发送消息',
      submitting: '发送中...',
      successTitle: '消息发送成功！',
      successMessage: '感谢您联系我们。我们将在24小时内回复。',
      sendAnother: '发送另一条消息',
      inquiryTypes: [
        { value: 'buyer', label: '买家兴趣 - 咖啡采购' },
        { value: 'supplier', label: '供应商申请' },
        { value: 'partnership', label: '合作机会' },
        { value: 'general', label: '一般咨询' }
      ]
    }
  };

  const t = content?.[language];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file && file?.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, attachment: language === 'en' ? 'File size must be less than 5MB' : '文件大小必须小于5MB' }));
      return;
    }
    setFormData(prev => ({ ...prev, attachment: file }));
    setErrors(prev => ({ ...prev, attachment: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData?.name?.trim()) {
      newErrors.name = language === 'en' ? 'Name is required' : '姓名为必填项';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = language === 'en' ? 'Email is required' : '电子邮件为必填项';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = language === 'en' ? 'Invalid email format' : '电子邮件格式无效';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = language === 'en' ? 'Phone number is required' : '电话号码为必填项';
    }

    if (!formData?.inquiryType) {
      newErrors.inquiryType = language === 'en' ? 'Please select inquiry type' : '请选择咨询类型';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = language === 'en' ? 'Message is required' : '消息为必填项';
    } else if (formData?.message?.trim()?.length < 20) {
      newErrors.message = language === 'en' ? 'Message must be at least 20 characters' : '消息必须至少20个字符';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Detect if Chinese buyer
      const chinesePhoneRegex = /^\+?86[-\s]?1[3-9]\d{9}$/;
      const isChineseBuyer = chinesePhoneRegex?.test(formData?.phone) || 
                             formData?.phone?.startsWith('+86') ||
                             formData?.phone?.startsWith('86');

      await buyerQueriesService?.create({
        name: formData?.name,
        email: formData?.email,
        phone: formData?.phone,
        company: formData?.company || null,
        inquiryType: formData?.inquiryType,
        message: formData?.message,
        isChineseBuyer: isChineseBuyer
      });

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiryType: '',
        message: '',
        attachment: null
      });
    } catch (error) {
      setErrors({ submit: error?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setSubmitSuccess(false);
  };

  if (submitSuccess) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">
          {t?.successTitle}
        </h3>
        <p className="text-muted-foreground mb-6">
          {t?.successMessage}
        </p>
        <Button onClick={handleSendAnother} variant="outline">
          {t?.sendAnother}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6 lg:p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
          {t?.title}
        </h2>
        <p className="text-muted-foreground">
          {t?.subtitle}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Input
            label={t?.name}
            type="text"
            name="name"
            placeholder={t?.namePlaceholder}
            value={formData?.name}
            onChange={handleChange}
            error={errors?.name}
            required
          />

          <Input
            label={t?.email}
            type="email"
            name="email"
            placeholder={t?.emailPlaceholder}
            value={formData?.email}
            onChange={handleChange}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Input
            label={t?.phone}
            type="tel"
            name="phone"
            placeholder={t?.phonePlaceholder}
            value={formData?.phone}
            onChange={handleChange}
            error={errors?.phone}
            required
          />

          <Input
            label={t?.company}
            type="text"
            name="company"
            placeholder={t?.companyPlaceholder}
            value={formData?.company}
            onChange={handleChange}
          />
        </div>

        <Select
          label={t?.inquiryType}
          placeholder={t?.inquiryPlaceholder}
          options={t?.inquiryTypes}
          value={formData?.inquiryType}
          onChange={(value) => {
            setFormData(prev => ({ ...prev, inquiryType: value }));
            if (errors?.inquiryType) {
              setErrors(prev => ({ ...prev, inquiryType: '' }));
            }
          }}
          error={errors?.inquiryType}
          required
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t?.message} <span className="text-error">*</span>
          </label>
          <textarea
            name="message"
            placeholder={t?.messagePlaceholder}
            value={formData?.message}
            onChange={handleChange}
            rows={6}
            className={`w-full px-3 py-2 bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none ${
              errors?.message ? 'border-error' : 'border-input'
            }`}
          />
          {errors?.message && (
            <p className="text-sm text-error mt-1">{errors?.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t?.attachment}
          </label>
          <p className="text-xs text-muted-foreground mb-2">{t?.attachmentDesc}</p>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
          />
          {errors?.attachment && (
            <p className="text-sm text-error mt-1">{errors?.attachment}</p>
          )}
        </div>

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
        >
          {isSubmitting ? t?.submitting : t?.submit}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;